/**
 * The dashboard's dialog renderer. Same service as the POS, MUI markup.
 *
 * MUI's `Dialog` already traps focus, locks scroll and restores focus, so the shared
 * `useDialogHost` is not used here — using both would mean two traps fighting. The shared half
 * that matters is the promise contract and the stack, which is identical.
 */

import { useState } from 'react';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { settle, useDialogStack, type DialogRequest } from '@pos/shared';

import { AppDialog } from '@/shared/ui/AppDialog';

export function DialogHost() {
  const stack = useDialogStack();

  return (
    <>
      {stack.map((request) => (
        <DialogView key={request.id} request={request} />
      ))}
    </>
  );
}

function DialogView({ request }: { request: DialogRequest }) {
  const [value, setValue] = useState(
    request.kind === 'prompt' ? (request.options.initialValue ?? '') : '',
  );
  const [error, setError] = useState<string | null>(null);

  const cancel = () => {
    settle(request.id, request.kind === 'alert' ? true : null);
  };

  const confirm = () => {
    if (request.kind === 'prompt') {
      const message = request.options.validate?.(value) ?? null;
      if (message !== null) {
        setError(message);
        return;
      }

      settle(request.id, value);
      return;
    }

    settle(request.id, true);
  };

  const dismissable =
    request.kind !== 'confirm' || (request.options.dismissOnBackdrop ?? true);

  return (
    <AppDialog
      open
      {...(dismissable && { onClose: cancel })}
      aria-labelledby={`${request.id}-title`}
    >
      <DialogTitle id={`${request.id}-title`}>{request.options.title}</DialogTitle>

      <DialogContent>
        {request.options.body !== undefined && (
          <DialogContentText sx={{ mb: request.kind === 'prompt' ? 2 : 0 }}>
            {request.options.body}
          </DialogContentText>
        )}

        {request.kind === 'prompt' && (
          <TextField
            autoFocus
            fullWidth
            label={request.options.label}
            placeholder={request.options.placeholder ?? ''}
            multiline={request.options.multiline ?? false}
            {...(request.options.multiline === true && { minRows: 3 })}
            value={value}
            error={error !== null}
            helperText={error ?? ' '}
            onChange={(event) => {
              setValue(event.target.value);
              if (error !== null) setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && request.options.multiline !== true) confirm();
            }}
          />
        )}
      </DialogContent>

      <DialogActions>
        {request.kind !== 'alert' && (
          <Button variant="outlined" onClick={cancel}>
            {request.options.cancelLabel ?? 'Cancel'}
          </Button>
        )}
        {/* Never red, whatever the tone. The rule allows one red button in the product — "Remove
            N units", which StockAdjustmentDialog draws itself — and "danger" here had quietly made
            some thirty confirms red: deactivate, revoke, cancel, sign out (case 169). */}
        <Button onClick={confirm} variant="contained">
          {request.options.confirmLabel ?? 'Confirm'}
        </Button>
      </DialogActions>
    </AppDialog>
  );
}
